/**
 * Putting a picture on a menu item.
 *
 * The phone does this with the Firebase SDK: it compresses the photo, uploads
 * it to `Menu Images/` in the project's storage bucket, and saves the download
 * URL on the item as `imageUrl`. The API itself never sees the file — it stores
 * a string.
 *
 * This does the same thing to the same bucket, so a picture added here shows up
 * on the phone and vice versa. It talks to Firebase Storage's REST endpoint
 * with plain axios instead of pulling the Firebase SDK into an Electron app
 * that has managed without it, and the URL it hands back is byte-identical in
 * shape to the SDK's.
 *
 * ── One-time setup ──────────────────────────────────────────────────────────
 * A browser upload is a cross-origin request, and Google Cloud Storage answers
 * one only if the bucket says it may. Until the bucket carries a CORS rule the
 * upload fails with a network error and no detail — the browser refuses to show
 * the response. To allow it, once:
 *
 *     gcloud storage buckets update gs://dalabtire-application.firebasestorage.app \
 *       --cors-file=cors.json
 *
 * with cors.json:
 *
 *     [{ "origin": ["http://localhost:3000", "file://"],
 *        "method": ["GET", "POST", "PUT"],
 *        "responseHeader": ["Content-Type"],
 *        "maxAgeSeconds": 3600 }]
 *
 * The bucket's rules must also allow the write. The phone uploads with no
 * Firebase sign-in, so they already do.
 */

import axios from 'axios';

/** The bucket the phone writes to — lib/firebase_options.dart, storageBucket. */
export const STORAGE_BUCKET = 'dalabtire-application.firebasestorage.app';

/** The folder the phone writes to — services/firebase_service.dart. */
const FOLDER = 'Menu Images';

/**
 * Stock pictures go in their own folder.
 *
 * Same bucket, same upload, same shape of URL — only the prefix differs, so a
 * store item's photo and a dish's photo never sit in the same listing. The
 * phone has no uploader for stock yet; when it gets one this is the folder to
 * point it at.
 */
export const STOCK_FOLDER = 'Stock Images';

/**
 * A restaurant's own picture — the one beside its name in the app and on
 * printed work that carries a letterhead.
 *
 * Its own folder for the same reason as the one above: a logo and a dish are
 * different things and should not share a listing. Same bucket, same upload,
 * same shape of URL, so a picture set here is readable everywhere the menu
 * images are.
 */
export const LOGO_FOLDER = 'Business Logos';

/**
 * Long edge, in pixels, and JPEG quality.
 *
 * The phone compresses at quality 50 and does not resize, which is fine for a
 * phone camera but not for whatever a desktop user drags in — a 4000px product
 * shot has no business being a menu thumbnail. Capping the long edge is the
 * difference between a 40KB file and a 3MB one, and the largest this image is
 * ever drawn is a 220px card.
 */
const MAX_EDGE = 1200;
const QUALITY = 0.6;

export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

/** Anything bigger than this is refused before it is read. */
export const MAX_INPUT_BYTES = 15 * 1024 * 1024;

/**
 * Draw the file into a canvas at a sane size and read it back as JPEG.
 *
 * Everything becomes a JPEG, including a PNG: the phone's uploads are all
 * `image/jpeg`, transparency means nothing on a photo tile, and one type keeps
 * the two clients writing the same kind of object.
 */
export function compressImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);

      const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height));
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      // A PNG with transparency would otherwise flatten onto black.
      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not read that image'))),
        'image/jpeg',
        QUALITY
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image'));
    };

    image.src = url;
  });
}

/** A local preview, so the tile fills in the instant a file is chosen. */
export function previewUrl(file) {
  return URL.createObjectURL(file);
}

/**
 * A name no other upload will take.
 *
 * The phone names the object after the file it picked, so two people uploading
 * `photo.jpg` overwrite each other's picture. A timestamp and a short random
 * tail cost nothing and remove that.
 */
function objectName(file, folder) {
  const stem = String(file.name || 'image')
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'image';
  const tail = Math.random().toString(36).slice(2, 8);
  return `${folder || FOLDER}/${stem}_${Date.now()}${tail}.jpg`;
}

/**
 * Compress, upload, and hand back the URL to store on the item.
 *
 * The returned URL is the same form the SDK returns —
 * `https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<path>?alt=media&token=<token>`
 * — which is what makes a picture uploaded here readable everywhere.
 */
export async function uploadMenuImage(file, { onProgress, folder } = {}) {
  if (!file) throw new Error('No image selected');
  if (file.size > MAX_INPUT_BYTES) throw new Error('That image is too large');

  const blob = await compressImage(file);
  const path = objectName(file, folder);

  let response;
  try {
    response = await axios.post(
      `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o` +
        `?uploadType=media&name=${encodeURIComponent(path)}`,
      blob,
      {
        headers: { 'Content-Type': 'image/jpeg' },
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) return;
          onProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );
  } catch (error) {
    // A blocked cross-origin request arrives here with no status and no body,
    // which reads as "network error" and sends people looking at their wifi.
    if (!error?.response) {
      throw new Error(
        'Could not reach image storage. If this is the first upload from this ' +
          'computer, the storage bucket still needs its CORS rule.'
      );
    }
    throw new Error(error.response?.data?.error?.message || 'Could not upload the image');
  }

  const token = String(response.data?.downloadTokens || '').split(',')[0];
  if (!token) throw new Error('Storage did not return a link for that image');

  return (
    `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o/` +
    `${encodeURIComponent(path)}?alt=media&token=${token}`
  );
}
